import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, withTranslation } from 'react-i18next';
import { ErrorBoundary, H1Title, ToggleMode } from '../../components';
import { useAPIError, useMode } from '../../context';
import { getAllMonetaryRatios, getOneMonetaryRatio } from '../matching/service';
import SectorsTable from './SectorsTable';
import { getMyUser, patchMyUser } from '../user/service';
import { toast } from 'react-toastify';
import { Text } from '@paygreen/paygreen-ui';

const Sectors = ({ t, i18n }) => {
    const { mode } = useMode();
    const { addError } = useAPIError();

    const [sectors, setSectors] = useState([]);
    const [userSectorId, setUserSectorId] = useState(null);
    const [userSector, setUserSector] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingUserData, setIsLoadingUserData] = useState(true);
    const [filters, setFilters] = useState({
        sectorName: '',
        pageIndex: 0,
        pageSize: 25,
        categoryName: '',
        activePageReset: false,
    });

    useEffect(() => {
        const retrieveUser = async () => {
            setIsLoadingUserData(true);
            const { data: user } = await getMyUser();
            if (!user?.defaultBusiness) {
                setIsLoadingUserData(false);
            }
            setUserSectorId(user?.defaultBusiness);
        };
        retrieveUser();
    }, []);

    useEffect(() => {
        const retrieveUserSector = async () => {
            setIsLoadingUserData(true);
            const {
                data: {
                    idMonetaryRatio,
                    monetaryRatioNameFrench,
                    monetaryRatioNameEnglish,
                    monetaryRatioCategoryNameFrench,
                    monetaryRatioCategoryNameEnglish,
                    value,
                },
            } = await getOneMonetaryRatio(userSectorId, mode);
            setUserSector({
                id: parseInt(idMonetaryRatio) || null,
                monetaryRatioNameFrench,
                monetaryRatioNameEnglish,
                monetaryRatioCategoryNameFrench,
                monetaryRatioCategoryNameEnglish,
                emission: value,
            });
            setIsLoadingUserData(false);
        };
        if (!userSectorId) {
            return setUserSector(null);
        }
        retrieveUserSector();
    }, [userSectorId, i18n, mode]);

    const updateUserSector = async (sectorId, name) => {
        const id = parseInt(sectorId);
        setIsLoadingUserData(true);
        setUserSectorId(id);
        try {
            await patchMyUser({ defaultBusiness: id });
            toast(
                <Text colorPallet="theme" textSize="xs">
                    <Trans i18nKey="sectors.toastSuccess">
                        ...
                        <Text
                            htmlTag="strong"
                            colorPallet="theme"
                            textSize="xs"
                        >
                            {{
                                sectorName: name,
                            }}
                        </Text>
                        ...
                    </Trans>
                </Text>,
            );
        } catch (error) {
            addError(
                error?.response?.data.detail,
                error?.response?.data.status,
            );
        }
    };

    const fetchData = useCallback(
        async ({ debouncedFilters }) => {
            setIsLoading(true);
            const params = {
                limit: debouncedFilters.pageSize,
                page: debouncedFilters.pageIndex + 1,
                search: debouncedFilters.sectorName,
            };
            try {
                const { data } = await getAllMonetaryRatios(params, mode);
                let ratios = [];
                if (
                    data?._embedded?.monetary_ratio &&
                    data?._embedded?.monetary_ratio?.length
                ) {
                    ratios = data._embedded.monetary_ratio.map((ratio) => ({
                        id: parseInt(ratio?.idMonetaryRatio),
                        monetaryRatioNameFrench: ratio?.monetaryRatioNameFrench,
                        monetaryRatioNameEnglish:
                            ratio?.monetaryRatioNameEnglish,
                        monetaryRatioCategoryNameFrench:
                            ratio?.monetaryRatioCategoryNameFrench,
                        monetaryRatioCategoryNameEnglish:
                            ratio?.monetaryRatioCategoryNameEnglish,
                        emission: ratio.value,
                    }));
                }
                setPageCount(data.page_count);
                setSectors(ratios);
            } catch (error) {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            } finally {
                setIsLoading(false);
            }
        },
        [mode, setSectors, addError],
    );

    return (
        <>
            <Helmet>
                <title>
                    {t('sectors.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('sectors.title')}>
                <ToggleMode />
            </H1Title>

            <ErrorBoundary>
                <SectorsTable
                    data={sectors}
                    fetchData={fetchData}
                    userSector={userSector}
                    updateUserSector={updateUserSector}
                    isLoading={isLoading}
                    isLoadingUserData={isLoadingUserData}
                    pageCount={pageCount}
                    setIsLoading={setIsLoading}
                    filters={filters}
                    setFilters={setFilters}
                />
            </ErrorBoundary>
        </>
    );
};

export default withTranslation('common')(Sectors);
